import { UpperCasePipe } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Assets } from '../assets';
import { FlagSize } from './flag.types';

@Component({
  standalone: true,
  imports: [MatTooltipModule, UpperCasePipe],
  selector: 'fl-flag',
  template: `
    <img
      alt="Flag of {{ displayCountryName | uppercase }}"
      class="FlagImage"
      i18n-alt="Flag alt text"
      [matTooltip]="displayCountryName"
      [attr.data-size]="size"
      [attr.data-size-tablet]="sizeTablet"
      [attr.data-size-desktop]="sizeDesktop"
      [attr.data-size-desktop-large]="sizeDesktopLarge"
      [src]="imagePath"
      (error)="onImageFetchError()"
    />
  `,
  styleUrls: ['./flag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlagComponent implements OnChanges {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private assets = inject(Assets);

  private forceUnknown = false;
  private countryDisplayNames?: Intl.DisplayNames;

  imagePath = '';

  @Input() countryCode = 'unknown';
  @Input() size = FlagSize.MID;
  @Input() sizeTablet?: FlagSize;
  @Input() sizeDesktop?: FlagSize;
  @Input() sizeDesktopLarge?: FlagSize;
  @Input() country: string;

  get displayCountryName(): string {
    if (this.country) {
      return this.country;
    }
    if (!this.countryCode || this.countryCode === 'unknown') {
      return '';
    }

    this.countryDisplayNames ??= new Intl.DisplayNames(undefined, {
      type: 'region',
    });

    return (
      this.countryDisplayNames.of(this.countryCode.toUpperCase()) ??
      this.countryCode.toUpperCase()
    );
  }

  onImageFetchError(): void {
    if (!this.forceUnknown) {
      // Well this sucks. Show the unknown flag for now.
      this.forceUnknown = true;
      this.imagePath = this.getFlagImage();
      this.changeDetectorRef.markForCheck();
    }
  }

  private getFlagImage(): string {
    if (this.forceUnknown || !this.countryCode) {
      return this.assets.getUrl('flags/unknown.png');
    }
    return this.assets.getUrl(`flags/${this.countryCode.toLowerCase()}.png`);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('countryCode' in changes) {
      // There's a possibility that the new `countryCode` can be a countryCode with an existing flag. Flip
      // forceUnknown to false to check.
      this.forceUnknown = false;
      this.imagePath = this.getFlagImage();
    }
  }
}
